import { z } from "zod";

export const ChatMessageReplySchema = z.object({
    id: z.string().min(1),
    title: z.string(),
    message: z.string(),
    imageUrl: z.string().optional(),
});
export type ChatMessageReplyPayload = z.infer<typeof ChatMessageReplySchema>;

export const TaggedContextSchema = z.discriminatedUnion("type", [
    z.object({
        type: z.literal("article"),
        id: z.string().min(1),
        article: z.object({
            title: z.string(),
            imageUrl: z.string().optional(),
        }),
        message: z.string().optional(),
    }),
    z.object({
        type: z.literal("booking"),
        id: z.string().min(1),
        booking: z.object({
            title: z.string(),
            imageUrl: z.string().optional(),
            optionId: z.string().min(1).optional(),
            optionTitle: z.string().optional(),
        }),
        message: z.string().optional(),
    }),
]);
export type TaggedContext = z.infer<typeof TaggedContextSchema>;

export const ChatReadyMessage = z.object({
    type: z.literal("chat.ready"),
});
export type ChatReadyMessage = z.infer<typeof ChatReadyMessage>;

export const ChatNavigateMessage = z.object({
    type: z.literal("chat.navigate"),
    url: z.url(),
});
export type ChatNavigateMessage = z.infer<typeof ChatNavigateMessage>;

export const ChatReplyStartMessage = z.object({
    type: z.literal("chat.reply.start"),
    reply: ChatMessageReplySchema,
});
export type ChatReplyStartMessage = z.infer<typeof ChatReplyStartMessage>;

export const ChatComposerDismissMessage = z.object({
    type: z.literal("chat.composer.dismiss"),
});
export type ChatComposerDismissMessage = z.infer<typeof ChatComposerDismissMessage>;

export const ChatDownloadMessage = z.object({
    type: z.literal("chat.download"),
    url: z.url(),
    filename: z.string().optional(),
    mimeType: z.string().optional(),
});
export type ChatDownloadMessage = z.infer<typeof ChatDownloadMessage>;

export const ChatLightboxMessage = z.object({
    type: z.literal("chat.lightbox"),
    open: z.boolean(),
});
export type ChatLightboxMessage = z.infer<typeof ChatLightboxMessage>;

/**
 * Asks the native iOS app to show chat images in its own viewer. Only sent
 * when the app sets `window.supportsNativeImageViewer`, so it is not part of
 * `ChatEmbedMessage`, which the Concierge iframe host receives.
 *
 * `images` holds objects so later fields stay additive for shipped apps.
 */
export const ChatImagesOpenMessage = z.object({
    type: z.literal("chat.images.open"),
    images: z.array(z.object({ url: z.url({ protocol: /^https?$/ }) })).min(1),
    index: z.number().int().nonnegative(),
});
export type ChatImagesOpenMessage = z.infer<typeof ChatImagesOpenMessage>;

export const ChatAnalyticsEventNameSchema = z.enum([
    "chat_reaction_sent",
    "chat_reply_sent",
    "chat_message_copied",
    "chat_audio_message_sent",
    "chat_offline_state_shown",
    "chat_notification_banner_shown",
    "chat_audio_transcription_shown",
]);
export type ChatAnalyticsEventName = z.infer<typeof ChatAnalyticsEventNameSchema>;

export const ChatAnalyticsMessage = z.object({
    type: z.literal("chat.analytics"),
    event_name: ChatAnalyticsEventNameSchema,
    raw_data: z.record(z.string(), z.unknown()).optional(),
});
export type ChatAnalyticsMessage = z.infer<typeof ChatAnalyticsMessage>;

export const ChatEmbedMessage = z.discriminatedUnion("type", [
    ChatReadyMessage,
    ChatNavigateMessage,
    ChatReplyStartMessage,
    ChatComposerDismissMessage,
    ChatDownloadMessage,
    ChatLightboxMessage,
    ChatAnalyticsMessage,
]);
export type ChatEmbedMessage = z.infer<typeof ChatEmbedMessage>;

export function parseChatEmbedMessageData(data: unknown): ChatEmbedMessage | null {
    const parsed = ChatEmbedMessage.safeParse(data);
    return parsed.success ? parsed.data : null;
}

export const ChatComposerStateMessage = z.object({
    type: z.literal("chat.composer.state"),
    height: z.number().nonnegative(),
    isActive: z.boolean(),
});
export type ChatComposerStateMessage = z.infer<typeof ChatComposerStateMessage>;

export const ChatDraftSubmitMessage = z.object({
    type: z.literal("chat.draft.submit"),
    message: z.string(),
    files: z.array(z.instanceof(File)).default([]),
    reply: ChatMessageReplySchema.optional(),
    context: TaggedContextSchema.optional(),
});
export type ChatDraftSubmitMessage = z.infer<typeof ChatDraftSubmitMessage>;

export const ChatHostMessage = z.discriminatedUnion("type", [
    ChatComposerStateMessage,
    ChatDraftSubmitMessage,
]);
export type ChatHostMessage = z.infer<typeof ChatHostMessage>;

export function parseChatHostMessageData(data: unknown): ChatHostMessage | null {
    const parsed = ChatHostMessage.safeParse(data);
    return parsed.success ? parsed.data : null;
}
